import debounce from 'debounce';
import KeenSlider from 'keen-slider';
import 'keen-slider/keen-slider.min.css';
class PostsSlider extends HTMLElement {
  constructor() {
    super();
    // elements
    this.keenSlider = null;
    this.slider = this.querySelector('.keen-slider');
    this.prevSelector = '[data-action=prev]';
    this.nextSelector = '[data-action=next]';
    this.refreshArrows = null; // Store reference to refreshArrows
    this.activeSlideIndex = 0; // Track currently active slide
    this.isMovingToSlide = false; // Flag to prevent double navigation
  }
  connectedCallback() {
    // bindings
    this.bndHandleResize = debounce(this.handleResize.bind(this), 250);
    this.bndHandleKeyDown = this.handleKeyDown.bind(this);
    this.bndHandleFocusIn = this.handleFocusIn.bind(this);

    // events
    this.onLoad();
    window.addEventListener('resize', this.bndHandleResize);

    // Listen for keyboard navigation and focus events
    this.addEventListener('keydown', this.bndHandleKeyDown);
    this.addEventListener('focusin', this.bndHandleFocusIn);
  }
  disconnectedCallback() {
    window.removeEventListener('resize', this.bndHandleResize);
    this.removeEventListener('keydown', this.bndHandleKeyDown);
    this.removeEventListener('focusin', this.bndHandleFocusIn);
    if (this.keenSlider && this.keenSlider.destroy) this.keenSlider.destroy();
  }
  onLoad() {
    this.initKeen();
  }
  handleResize() {
    if (this.refreshArrows) {
      this.refreshArrows();
    }
  }

  // Check if a slide is visible (either fully or partially visible)
  isSlideVisible(slideIndex) {
    if (!this.keenSlider || !this.keenSlider.track || !this.keenSlider.track.details) {
      return false;
    }
    const details = this.keenSlider.track.details;

    // If slideIndex is out of range
    if (slideIndex < 0 || slideIndex >= details.slides.length) {
      return false;
    }
    const slideDetails = details.slides[slideIndex];
    return slideDetails && slideDetails.portion > 0;
  }

  // Handle focus events - detect when an element receives focus
  handleFocusIn(e) {
    if (this.isMovingToSlide || !this.keenSlider) return;
    const focusedElement = e.target;
    const slide = focusedElement.closest('.keen-slider__slide');
    if (!slide) return;
    const slides = Array.from(this.querySelectorAll('.keen-slider__slide'));
    const slideIndex = slides.indexOf(slide);
    if (slideIndex === -1) return;

    // If the focused slide is not visible, move to it without scrolling the viewport
    if (!this.isSlideVisible(slideIndex)) {
      // Set a flag to prevent double navigation
      this.isMovingToSlide = true;

      // Store current scroll position
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      // Move to the slide
      this.moveToSlide(slideIndex);

      // Restore scroll position after browser tries to scroll
      setTimeout(() => {
        window.scrollTo(scrollX, scrollY);
        this.isMovingToSlide = false;
      }, 10);
    }
  }

  // Method to move to a slide, handling edge cases
  moveToSlide(slideIndex) {
    if (!this.keenSlider) return;
    const details = this.keenSlider.track.details;
    if (!details) return;

    // For slides at the very end, we need special handling
    if (slideIndex > details.maxIdx) {
      // Move to the maximum possible position
      this.keenSlider.moveToIdx(details.maxIdx);

      // Keen slider has a limitation with the very last slides in auto-width mode
      // We manually adjust the internal position to show the target slide
      const targetPosition = details.slidesLength - 1;
      setTimeout(() => {
        this.keenSlider.track.to(targetPosition);
      }, 50);
    } else {
      // For normal slides, just use moveToIdx
      this.keenSlider.moveToIdx(slideIndex);
    }
  }

  // Get all focusable elements within a container
  getFocusableElements(container) {
    const focusableSelectors = ['a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])', 'area[href]'];
    return Array.from(container.querySelectorAll(focusableSelectors.join(','))).filter(el => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true' && el.offsetParent !== null); // Check for visibility
  }

  // Handle keyboard tab navigation
  handleKeyDown(e) {
    // Only handle Tab key
    if (e.key !== 'Tab' || !this.keenSlider || this.isMovingToSlide) return;

    // Find the currently focused element
    const activeElement = document.activeElement;

    // Find which slide contains the focused element
    const currentSlide = activeElement.closest('.keen-slider__slide');

    // If we're not in a slide, allow normal tab behavior
    if (!currentSlide) return;
    const slides = Array.from(this.querySelectorAll('.keen-slider__slide'));
    const currentSlideIndex = slides.indexOf(currentSlide);

    // If we can't determine the slide, allow normal tab behavior
    if (currentSlideIndex === -1) return;

    // Get all focusable elements in the current slide
    const focusableElements = this.getFocusableElements(currentSlide);
    const currentElementIndex = focusableElements.indexOf(activeElement);
    const isForward = !e.shiftKey;

    // Check if we're at the edge of the current slide
    const isLastElementInSlide = currentElementIndex === focusableElements.length - 1;
    const isFirstElementInSlide = currentElementIndex === 0;

    // If we're at the edge of the slide and tabbing to the next/prev slide
    if (isLastElementInSlide && isForward || isFirstElementInSlide && !isForward) {
      // Determine target slide index
      const nextSlideIndex = isForward ? currentSlideIndex + 1 : currentSlideIndex - 1;

      // If there's no next/prev slide, allow normal tab behavior
      if (nextSlideIndex < 0 || nextSlideIndex >= slides.length) return;

      // Check if the target slide is currently visible
      if (!this.isSlideVisible(nextSlideIndex)) {
        // Prevent default tab behavior
        e.preventDefault();

        // Store current scroll position
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        // Set a flag to prevent double navigation
        this.isMovingToSlide = true;

        // Move to that slide
        this.moveToSlide(nextSlideIndex);

        // After the slide transition, focus the first/last focusable element in the target slide
        setTimeout(() => {
          const targetSlide = slides[nextSlideIndex];
          const targetElements = this.getFocusableElements(targetSlide);
          if (targetElements.length > 0) {
            // Focus the first or last element depending on tab direction
            const targetElement = isForward ? targetElements[0] : targetElements[targetElements.length - 1];
            targetElement.focus({
              preventScroll: true
            });

            // Restore scroll position
            window.scrollTo(scrollX, scrollY);
          }
          this.isMovingToSlide = false;
        }, 150); // Wait for the slide transition
      }
    }
  }
  initKeen() {
    if (!this.slider) return;
    this.keenSlider = new KeenSlider(this.slider, {
      loop: false,
      mode: 'free-snap',
      slides: {
        perView: 'auto',
        spacing: 16
      },
      created: () => {
        this.slider.classList.add('is-ready');
      },
      breakpoints: {
        '(min-width: 48em)': {
          slides: {
            perView: 'auto',
            spacing: 32
          }
        }
      }
    }, [slider => {
      const next = this.querySelector(this.nextSelector);
      const prev = this.querySelector(this.prevSelector);
      const refreshArrows = () => {
        const details = slider.track.details;
        const current = details?.rel ?? 0;
        prev?.removeAttribute('disabled');
        next?.removeAttribute('disabled');
        if (current === 0) {
          prev?.setAttribute('disabled', 'disabled');
        }
        if (current >= details.maxIdx) {
          next?.setAttribute('disabled', 'disabled');
        }
      };
      // Store reference to refreshArrows function
      this.refreshArrows = refreshArrows;
      slider.on('created', () => {
        refreshArrows();
        slider.container?.classList.add('is-ready');
        next?.addEventListener('click', () => {
          slider.next();
        });
        prev?.addEventListener('click', () => {
          slider.prev();
        });

        // Focus on the first element if marked with is-focus class
        const focusSlide = this.querySelector('.keen-slider__slide.is-focus');
        if (focusSlide) {
          const focusElements = this.getFocusableElements(focusSlide);
          if (focusElements.length > 0) {
            // Use setTimeout to ensure the slide is visible first
            setTimeout(() => {
              focusElements[0].focus({
                preventScroll: true
              });
            }, 100);
          }
        }
      });
      slider.on('slideChanged', () => {
        refreshArrows();
        const details = slider.track.details;
        this.activeSlideIndex = details.rel;
        if (this.current) {
          this.current.textContent = details.rel + 1;
        }
      });
    }]);
  }
}
customElements.define('posts-slider', PostsSlider);